import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface ModGroupOpenApi {
  /** Has the agent explicitly opened this group? */
  isOpenedByAgent: (pathToItem: string) => boolean;
  toggle: (pathToItem: string) => void;
}

const noop: ModGroupOpenApi = {
  isOpenedByAgent: () => false,
  toggle: () => undefined,
};

const ModGroupOpenContext = createContext<ModGroupOpenApi>(noop);

/**
 * HITL-1616 — which option drawers the agent has opened.
 *
 * ## Why this is component state and not `dialogSlice`
 *
 * A group is open when ANY of four things is true, and three of them already
 * live in Redux and already reach `ModGroup`:
 *
 * - the agent tapped the row — the only thing this context holds;
 * - `dialogSlice.selectedModGroupPath` matches, which `ModGroup` already
 *   receives as `isSelectedToFocus` — that covers Shift+W/S/A/D
 *   (`useModifierGroupNavigation.hooks.ts:142`) AND clicking a mod group in the
 *   cart (`CartItemModGroups.tsx:140`);
 * - `dialogSlice.highlightedModifier` matches, which `ModGroup` already computes
 *   as `isHighlighted` — that covers the omnibar's modifier search
 *   (`AutocompleteComp.tsx:1109`);
 * - the HITL-1433 auto-reveal targeted it.
 *
 * So every one of the four existing "scroll to that group" behaviours opens the
 * drawer by construction, with no new action, no new reducer, and nothing for a
 * caller to remember. Adding an open-state field to `dialogSlice` would mean
 * teaching each of those four to write it, which is exactly the kind of list
 * that goes stale.
 *
 * Scoped per cart item: the provider sits in `CartItemDetail`, so opening a
 * drawer on one item says nothing about the next item the agent selects.
 */
export const ModGroupOpenProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [openPaths, setOpenPaths] = useState<Record<string, boolean>>({});

  const isOpenedByAgent = useCallback(
    (pathToItem: string) => !!openPaths[pathToItem],
    [openPaths]
  );

  const toggle = useCallback((pathToItem: string) => {
    setOpenPaths((current) => ({
      ...current,
      [pathToItem]: !current[pathToItem],
    }));
  }, []);

  const api = useMemo(
    () => ({ isOpenedByAgent, toggle }),
    [isOpenedByAgent, toggle]
  );

  return (
    <ModGroupOpenContext.Provider value={api}>
      {children}
    </ModGroupOpenContext.Provider>
  );
};

export const useModGroupOpen = () => useContext(ModGroupOpenContext);

/**
 * THE rule this whole story turns on: a closed drawer is HIDDEN, never
 * unmounted.
 *
 * `ModGroup.tsx:158-202` applies a group's default modifier selections from a
 * mount effect, and for a nested group revealed after add-time it is the only
 * mechanism that does so — `processDefaultSelectedModifiers` runs once at
 * add-item time and cascades only through groups that already exist. Unmount a
 * closed group and the cart's contents become a function of which rows the agent
 * happened to open; in `Intercept` that difference reaches the POS through
 * `OrderReview.tsx:115`.
 *
 * `ConsoleCard`'s `keepBodyMounted` does the same thing for `OrderReview`, for
 * the same reason.
 */
export const HIDDEN_WHEN_CLOSED: React.CSSProperties = Object.freeze({
  display: 'none',
});

export default ModGroupOpenContext;
